import {
  Collapse,
  Divider,
  IconButton,
  ListItem,
  ListItemAvatar,
  ListItemButton,
  ListItemText,
  Stack,
  Typography,
} from '@mui/material';
import { useTranslations } from '@/i18n/use-translations';
import type { ContributorSummaryDto } from '@/types/dtos/contributors/response/contributor-summary.dto';
import type { ContributorListSummaryDto } from '@/types/dtos/contributors/response/contributor-list-summary.dto';
import type { EventSummaryDto } from '@/types/dtos/events/response/event-summary.dto';
import type { EventListSummaryDto } from '@/types/dtos/contributors/response/event-list-summary.dto';
import {
  mergeRoleMedia,
  resolveContributorMediaProcessingIssue,
  roleMediaNeedsContributorUploadAction,
} from '@/components/user-dashboard/contributions/contributor-role-media.utils.ts';
import { addDays, format, parseISO } from 'date-fns';
import { eventTypeToIcon } from '@/components/events/helpers/event-type-to-icon';
import type { EventType } from '@/types/dtos/events/event-type.enum';
import { useMemo, useState } from 'react';
import { FiberManualRecord, OpenInNew } from '@mui/icons-material';
import { useNavigate } from 'react-router';
import { useFeatureAccess } from '@/hooks/use-feature-access';
import { REMOTE_SPEAKERSHIP_FEATURE } from '@/constants/feature-flags';
import { slotNeedsRemoteSpeakerConfirmation } from '@/utils/remote-speaker-status.utils';

type ContributorForList = ContributorSummaryDto | ContributorListSummaryDto;
type EventForList = EventSummaryDto | EventListSummaryDto;

const hasOpenAcceptanceOrRemoteTasks = (contributor: ContributorForList, remoteSpeakershipAvailable: boolean) =>
  contributor.roles?.some(
    (role) =>
      !role.acceptedAt ||
      (role.slots?.some(
        (slot) => !slot.acceptedAt || (remoteSpeakershipAvailable && slotNeedsRemoteSpeakerConfirmation(slot)),
      ) ??
        false),
  ) ?? false;

const contributorNeedsMediaUploadAction = (contributor: ContributorForList, event?: EventForList) => {
  if (!contributor.roles?.length) return false;

  return contributor.roles.some((role) => {
    if (!role.acceptedAt) return false;
    const roleMedia = mergeRoleMedia({
      role,
      contributorMedia: contributor.media ?? [],
      eventId: event?.id,
    });
    return roleMediaNeedsContributorUploadAction(roleMedia).needsUploadAction;
  });
};

const getContributorMediaProcessingIssue = (contributor: ContributorForList, event?: EventForList) => {
  if (!contributor.roles?.length) {
    return resolveContributorMediaProcessingIssue(contributor.media ?? []);
  }

  let hasWarning = false;
  for (const role of contributor.roles) {
    if (!role.acceptedAt) {
      continue;
    }
    const roleMedia = mergeRoleMedia({
      role,
      contributorMedia: contributor.media ?? [],
      eventId: event?.id,
    });
    const issue = resolveContributorMediaProcessingIssue(roleMedia);
    if (issue === 'error') return 'error';
    if (issue === 'warning') hasWarning = true;
  }
  return hasWarning ? 'warning' : undefined;
};

const hasValidAcceptedEventRole = (contributor: ContributorForList) => {
  if (!contributor.roles || contributor.roles.length === 0) {
    return false;
  }

  return contributor.roles.some((role) => {
    if (!role.acceptedAt) {
      return false;
    }

    if (role.entityType !== 'Event') {
      return false;
    }

    return !!role.entityId || !!contributor.event?.id;
  });
};

const formatSecondaryText = (event: EventForList | undefined, t: ReturnType<typeof useTranslations>) => {
  const start = event?.startDate ? format(parseISO(event.startDate), 'dd.MM.yy') : t('myEventsList.unknown');
  const end =
    event?.durationDays && event?.startDate
      ? format(addDays(parseISO(event.startDate), event.durationDays - 1), 'dd.MM.yy')
      : t('myEventsList.unknown');
  return `${start} - ${end} ${event?.location ? `| ${event.location}` : ''}`;
};

const buildEventLink = (event?: EventForList) => {
  if (!event?.id) return null;

  let navigateToUrl = `/event/${event.id}`;
  if (!event.multiFlow && event?.flows) {
    const singleFlow = event.flows[0];
    if (singleFlow) navigateToUrl = `/flow/${singleFlow.id}`;
  }

  return navigateToUrl;
};

const useContributionListIndicators = (contributor: ContributorForList, event?: EventForList) => {
  const { hasAccess: hasRemoteSpeakershipAccess, isLoading: isRemoteSpeakershipAccessLoading } =
    useFeatureAccess(REMOTE_SPEAKERSHIP_FEATURE);
  const remoteSpeakershipAvailable = !isRemoteSpeakershipAccessLoading && hasRemoteSpeakershipAccess;

  const hasOpenTasks = useMemo(
    () => hasOpenAcceptanceOrRemoteTasks(contributor, remoteSpeakershipAvailable),
    [contributor, remoteSpeakershipAvailable],
  );
  const hasMissingMedia = useMemo(() => contributorNeedsMediaUploadAction(contributor, event), [contributor, event]);
  const processingIssue = useMemo(() => getContributorMediaProcessingIssue(contributor, event), [contributor, event]);
  const needsAction = hasOpenTasks || hasMissingMedia || Boolean(processingIssue);
  const statusDotColor: 'primary' | 'warning' | 'error' =
    processingIssue === 'error' ? 'error' : processingIssue === 'warning' ? 'warning' : 'primary';

  return { needsAction, statusDotColor };
};

export const ContributionListItem = ({
  contributor,
  event,
  isSelected,
}: {
  contributor: ContributorForList;
  event?: EventForList;
  isSelected: boolean;
}) => {
  const t = useTranslations();
  const navigate = useNavigate();
  const [open] = useState(false);
  const { needsAction, statusDotColor } = useContributionListIndicators(contributor, event);

  const handleListItemClick = () => {
    navigate(`/contributions/${contributor.id}`);
  };

  return (
    <>
      <ListItemButton
        onClick={handleListItemClick}
        sx={{ padding: 0, backgroundColor: isSelected ? 'action.selected' : 'transparent' }}
      >
        <ListItem key={contributor.id}>
          {needsAction && <FiberManualRecord color={statusDotColor} sx={{ fontSize: 16, mr: 1, flexShrink: 0 }} />}
          <ListItemAvatar sx={{ minWidth: 40, color: 'action.active' }}>
            {eventTypeToIcon(event?.type as EventType)}
          </ListItemAvatar>
          <ListItemText
            primary={event?.title}
            secondary={formatSecondaryText(event, t)}
            primaryTypographyProps={{ color: 'action.active' }}
            secondaryTypographyProps={{ color: 'action.active' }}
          />
        </ListItem>
      </ListItemButton>
      <Collapse in={open} timeout="auto" unmountOnExit>
        <Stack sx={{ maxHeight: '120px', overflow: 'auto', my: 1 }}>
          <Typography variant="caption" color="text.secondary">
            {t('myEventsList.contributor.eventInfo')}
          </Typography>
          <Typography variant="body2">{event?.description || t('myEventsList.unknown')}</Typography>
        </Stack>
      </Collapse>
      <Divider />
    </>
  );
};

export const ContributionCarouselCardContent = ({
  contributor,
  event,
}: {
  contributor: ContributorForList;
  event?: EventForList;
}) => {
  const t = useTranslations();
  const navigate = useNavigate();
  const canOpenEvent = useMemo(() => hasValidAcceptedEventRole(contributor), [contributor]);
  const { needsAction, statusDotColor } = useContributionListIndicators(contributor, event);

  const handleOpenEvent = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    const url = buildEventLink(event);
    if (url) navigate(url);
  };

  return (
    <>
      <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 2 }}>
        {needsAction && <FiberManualRecord color={statusDotColor} sx={{ fontSize: 16, flexShrink: 0 }} />}
        {eventTypeToIcon(event?.type as EventType)}
        <Stack flex={1} minWidth={0}>
          <Typography variant="h6" sx={{ fontWeight: 'bold', overflow: 'hidden', textOverflow: 'ellipsis' }}>
            {event?.title || t('myEventsList.unknown')}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {formatSecondaryText(event, t)}
          </Typography>
        </Stack>
        {canOpenEvent && (
          <IconButton onClick={handleOpenEvent} size="small" sx={{ color: 'primary.main' }}>
            <OpenInNew />
          </IconButton>
        )}
      </Stack>
      {event?.description && (
        <Typography variant="body2" color="text.secondary" sx={{ flex: 1, overflow: 'auto' }}>
          {event.description}
        </Typography>
      )}
    </>
  );
};

export default ContributionListItem;
